import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const ARTICLE_IMAGES: Record<string, string> = {
  'how-to-access-91-web': 'https://readdy.ai/api/search-image?query=Modern%20laptop%20displaying%20a%20clean%20web%20browser%20interface%20with%20login%20page%2C%20minimalist%20desk%20setup%20with%20warm%20ambient%20lighting%2C%20soft%20natural%20light%20from%20window%2C%20editorial%20photography%20style%2C%20warm%20neutral%20tones&width=800&height=500&seq=cat-article-access&orientation=landscape',
  'fix-91-web-not-opening': 'https://readdy.ai/api/search-image?query=A%20person%20troubleshooting%20on%20laptop%20with%20browser%20error%20page%20displayed%2C%20warm%20modern%20home%20office%20setting%2C%20soft%20daylight%20through%20blinds%2C%20editorial%20atmosphere%2C%20warm%20beige%20and%20wood%20tones&width=800&height=500&seq=cat-article-fix&orientation=landscape',
  '91-web-new-features-2026': 'https://readdy.ai/api/search-image?query=Modern%20clean%20web%20interface%20design%20showcase%20on%20a%20large%20monitor%20screen%2C%20bright%20airy%20creative%20office%2C%20minimal%20Scandinavian%20style%2C%20natural%20lighting%2C%20warm%20neutral%20palette&width=800&height=500&seq=cat-article-features&orientation=landscape',
  '91-web-speed-optimization': 'https://readdy.ai/api/search-image?query=Close-up%20of%20a%20laptop%20keyboard%20with%20hands%20typing%2C%20speed%20and%20efficiency%20theme%2C%20modern%20minimalist%20desk%20setup%2C%20soft%20warm%20lighting%2C%20shallow%20depth%20of%20field&width=800&height=500&seq=cat-article-speed&orientation=landscape',
  '91-web-account-security': 'https://readdy.ai/api/search-image?query=Digital%20security%20concept%20with%20padlock%20icon%20floating%20over%20a%20laptop%20screen%2C%20modern%20cyber%20security%20theme%2C%20clean%20desk%20setting%2C%20warm%20tones%2C%20professional%20atmosphere&width=800&height=500&seq=cat-article-security&orientation=landscape',
  '91-web-complete-platform-guide': 'https://readdy.ai/api/search-image?query=Comprehensive%20digital%20dashboard%20on%20an%20ultra-wide%20monitor%2C%20organized%20interface%20with%20multiple%20panels%2C%20modern%20creative%20workspace%2C%20warm%20ambient%20lighting%2C%20clean%20minimalist&width=800&height=500&seq=cat-article-guide&orientation=landscape',
  '91-web-vs-app-comparison': 'https://readdy.ai/api/search-image?query=Split%20screen%20showing%20web%20browser%20on%20left%20and%20mobile%20phone%20on%20right%20displaying%20same%20platform%2C%20modern%20desk%20setup%2C%20warm%20natural%20light%2C%20balanced%20composition&width=800&height=500&seq=cat-article-compare&orientation=landscape',
  '91-web-register-guide': 'https://readdy.ai/api/search-image?query=Fresh%20start%20with%20laptop%20showing%20registration%20form%20on%20screen%2C%20clean%20modern%20desk%20with%20small%20plant%20and%20coffee%2C%20soft%20morning%20light%2C%20warm%20inviting%20atmosphere&width=800&height=500&seq=cat-article-register&orientation=landscape',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'access-tutorial');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <Navbar />
      <main>
        <div className="pt-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                ...(currentCategory ? [{ label: currentCategory.name }] : []),
              ]}
            />
          </div>
        </div>

        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                {currentCategory?.name || '内容分类'}
              </h1>
              <p className="text-foreground-600">
                {currentCategory?.description || '浏览所有分类内容'}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCategoryChange(cat.slug);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles Grid */}
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={ARTICLE_IMAGES[article.slug] || ARTICLE_IMAGES['how-to-access-91-web']}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="text-sm font-medium text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime} 分钟
                        </span>
                        <span>{article.publishDate}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || '分类'} - 91网页版入口`,
            description: currentCategory?.description || '',
            url: `https://91web.example.com/category/${activeCategory}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '91网页版入口',
              url: 'https://91web.example.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://91web.example.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentCategory?.name || '分类',
                item: `https://91web.example.com/category/${activeCategory}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}